import React from 'react';
import classNames from 'classnames';
import { Panel, PanelProps } from './Panel';
import styles from './Tabs.module.scss';
import getTabId from './getTabId';

export interface TabsProps {
	readonly className?: string;
	readonly variant?: 'default' | 'contained';
	readonly children: React.ReactElement<PanelProps> | readonly React.ReactElement<PanelProps>[];
}

/**
 * Generic Tabs component used by both CMS and regular features. Has to be fed with TabPanels as children.
 */

export const Tabs: React.FC<TabsProps> & { Panel: React.FC<PanelProps> } = ({
	variant = 'default',
	children,
	className,
}: TabsProps) => {
	const [selectedTab, setSelectedTab] = React.useState(0);
	const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

	const handleSelectTab = React.useCallback((index: number): void => {
		setSelectedTab(index);
	}, []);

	// Helper to calculate correct next or prev tab
	const handleTabSelection = React.useCallback(
		(firstTabInRound: number, nextTab: number, lastTabInRound: number): void => {
			const tabToSelect = selectedTab === lastTabInRound ? firstTabInRound : nextTab;
			setSelectedTab(tabToSelect);
			tabRefs.current[tabToSelect]?.focus();
		},
		[selectedTab],
	);

	// Handle navigation with arrow keys
	const handleKeyPress = React.useCallback(
		(event: React.KeyboardEvent): void => {
			const tabCount = React.Children.count(children) - 1;

			if (event.key === 'ArrowLeft') {
				const last = tabCount;
				const next = selectedTab - 1;
				handleTabSelection(last, next, 0);
			}
			if (event.key === 'ArrowRight') {
				const first = 0;
				const next = selectedTab + 1;
				handleTabSelection(first, next, tabCount);
			}
		},
		[children, selectedTab, handleTabSelection],
	);

	return (
		<div className={classNames(className, { [styles.isContained]: variant === 'contained' })}>
			{/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
			<div className={styles.tabList} role="tablist" onKeyDown={handleKeyPress}>
				{React.Children.map(children, (tab: React.ReactElement<PanelProps>, index) => {
					const isSelected = selectedTab === index;
					const tabId = getTabId(tab.props.name);

					return (
						<button
							className={styles.tab}
							type="button"
							role="tab"
							aria-selected={isSelected}
							aria-controls={`${tabId}-tab`}
							id={tabId}
							tabIndex={!isSelected ? -1 : undefined}
							onClick={(): void => handleSelectTab(index)}
							ref={(ref): HTMLButtonElement | null => (tabRefs.current[index] = ref)}
							key={tabId + index}
						>
							{tab.props.name}
						</button>
					);
				})}
			</div>

			{React.Children.map(children, (child: React.ReactElement<PanelProps>, index) => {
				const isSelected = selectedTab === index;

				return React.cloneElement(child, {
					hidden: !isSelected,
				});
			})}
		</div>
	);
};

Tabs.Panel = Panel;
